/*
 * In-app replacements for the browser's alert() and confirm().
 *
 * The native boxes show the browser's own look and language (often English) and block the
 * page. These show the game's dialog, translated, and return a promise:
 *
 *   if (await confirmDialog({message: 'You will pay ${0} M€ more than the cost. Pay anyway?', params: [3], okLabel: 'Pay'})) {...}
 *   await alertDialog({title: 'Error', message: 'Specify a game id'});
 *
 * One dialog shows at a time; later requests wait for it. The dialog mounts itself into
 * <body> the first time it's needed, so it works on every screen.
 */
import { createApp, reactive } from 'vue';
import AppDialog from '@/client/components/common/AppDialog.vue';
export const dialogState = reactive({
    current: undefined,
    queue: [],
});
let host;
let app;
function ensureHost() {
    if (typeof document === 'undefined') {
        return;
    }
    if (host !== undefined && host.isConnected) {
        return;
    }
    app?.unmount();
    host = document.createElement('div');
    host.className = 'app-dialog-host';
    document.body.appendChild(host);
    app = createApp(AppDialog);
    app.mount(host);
}
function request(kind, options) {
    const opts = typeof options === 'string' ? { message: options } : options;
    return new Promise((resolve) => {
        const req = { ...opts, kind, resolve };
        if (dialogState.current === undefined) {
            dialogState.current = req;
        }
        else {
            dialogState.queue.push(req);
        }
        ensureHost();
    });
}
/** Shows a message with an OK button. Resolves when it's closed. */
export async function alertDialog(options) {
    await request('alert', options);
}
/** Asks a question. Resolves true for OK (Enter), false for Cancel (Esc). */
export function confirmDialog(options) {
    return request('confirm', options);
}
/** Closes the dialog on screen with this answer, and shows the next one. Called by AppDialog. */
export function answerDialog(ok) {
    const current = dialogState.current;
    if (current === undefined) {
        return;
    }
    dialogState.current = dialogState.queue.shift();
    current.resolve(current.kind === 'confirm' ? ok : true);
}
/** For tests: drop every pending dialog (answering Cancel) and the mounted host. */
export function resetDialogsForTests() {
    while (dialogState.current !== undefined) {
        answerDialog(false);
    }
    app?.unmount();
    app = undefined;
    host?.remove();
    host = undefined;
}
